import { Alert, Box, Button, CircularProgress, Snackbar } from '@mui/material';
import { useStytch } from '@platform/auth';
import {
  consumeAuthRedirectTarget,
  isOAuthAuthorizeRedirectTarget,
  peekAuthRedirectTarget,
  resolveAuthenticatedRedirectTarget,
} from '@platform/auth/authRedirect';
import AuthLoadingScreen from '@platform/auth/components/AuthLoadingScreen';
import AuthDevPanel from '@platform/auth/dev/AuthDevPanel';
import { clearStytchCookies } from '@platform/auth/providers/clearStytchCookies';
import MentoLogoWhite from '@shared/ui/components/Logo/MentoLogoWhite';
import { useStytchMemberSession } from '@stytch/react/b2b';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { tokenService } from '../../services/tokenService';
import { useAppSelector } from '../../store/hooks';

type DiscoveryProvider = 'google' | 'microsoft';

const DEFAULT_DISCOVERY_PROVIDERS: DiscoveryProvider[] = ['google'];

function resolveDiscoveryProviders(rawProviders: string[] | undefined): DiscoveryProvider[] {
  const normalized = Array.from(
    new Set(
      (rawProviders || [])
        .map((provider) => provider.trim().toLowerCase())
        .filter(
          (provider): provider is DiscoveryProvider =>
            provider === 'google' || provider === 'microsoft'
        )
    )
  );

  return normalized.length > 0 ? normalized : DEFAULT_DISCOVERY_PROVIDERS;
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function MicrosoftMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="2" y="2" width="9" height="9" fill="#F25022" />
      <rect x="13" y="2" width="9" height="9" fill="#7FBA00" />
      <rect x="2" y="13" width="9" height="9" fill="#00A4EF" />
      <rect x="13" y="13" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

interface LoginPageContentProps {
  hasStytchMemberSession: boolean;
  stytchMemberSessionInitialized: boolean;
}

function LoginPageContent({
  hasStytchMemberSession,
  stytchMemberSessionInitialized,
}: LoginPageContentProps) {
  const { stytch, ready: stytchReady } = useStytch();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const isUserResolved = useAppSelector((s) => s.auth.isUserResolved);
  const status = useAppSelector((s) => s.auth.status);
  const sessionMessage = useAppSelector((s) => s.auth.sessionMessage);
  const user = useAppSelector((s) => s.auth.user);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [dismissedSessionMessage, setDismissedSessionMessage] = useState(false);
  const [discoveryProviders, setDiscoveryProviders] = useState<DiscoveryProvider[]>(() =>
    resolveDiscoveryProviders(
      typeof window !== 'undefined' ? window.__CONFIG__?.STYTCH_DISCOVERY_PROVIDERS : undefined
    )
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: The sessionMessage dependency is implicit
  useEffect(() => {
    setDismissedSessionMessage(false);
  }, [sessionMessage]);

  useEffect(() => {
    const syncProviders = () => {
      setDiscoveryProviders(
        resolveDiscoveryProviders(
          typeof window !== 'undefined' ? window.__CONFIG__?.STYTCH_DISCOVERY_PROVIDERS : undefined
        )
      );
    };

    syncProviders();
    window.addEventListener('mento:config-loaded', syncProviders);
    return () => {
      window.removeEventListener('mento:config-loaded', syncProviders);
    };
  }, []);

  const stytchPublicToken =
    (typeof window !== 'undefined' && window.__CONFIG__?.STYTCH_PUBLIC_TOKEN) ||
    import.meta.env.VITE_STYTCH_PUBLIC_TOKEN;

  const handleProviderLogin = async (provider: DiscoveryProvider) => {
    if (!stytch) {
      setError('Stytch client not initialized');
      return;
    }
    setIsAuthenticating(true);
    setError(null);

    // Clear any stale Stytch session state before starting a fresh login.
    // Stale cookies from expired or deleted org sessions cause
    // "unauthorized_credentials" errors during the Discovery OAuth flow.
    try {
      await stytch.session?.revoke?.();
    } catch {
      // best-effort: session may already be invalid
    }
    await clearStytchCookies();

    try {
      if (provider === 'microsoft') {
        stytch.oauth.microsoft.discovery.start({
          discovery_redirect_url: `${window.location.origin}/auth/discovery-callback`,
        });
        return;
      }

      stytch.oauth.google.discovery.start({
        discovery_redirect_url: `${window.location.origin}/auth/discovery-callback`,
      });
    } catch (unknownError: unknown) {
      const message = unknownError instanceof Error ? unknownError.message : null;
      const providerLabel = provider === 'microsoft' ? 'Microsoft' : 'Google';
      setError(message || `Failed to start ${providerLabel} login`);
      setIsAuthenticating(false);
    }
  };

  const handleDevLogin = async (email = 'dev@localhost') => {
    setIsAuthenticating(true);
    setError(null);
    try {
      const token = await tokenService.devLogin(email);
      if (!token) {
        setError('Dev login failed - check backend logs');
      }
    } catch (unknownError: unknown) {
      const message = unknownError instanceof Error ? unknownError.message : null;
      setError(message || 'Dev login failed');
    } finally {
      setIsAuthenticating(false);
    }
  };

  if (isAuthenticated && !isUserResolved) {
    return <AuthLoadingScreen overlay testId="login-auth-loading-screen" />;
  }

  const savedRedirectTarget = peekAuthRedirectTarget();
  const needsStytchOAuthLogin = isOAuthAuthorizeRedirectTarget(savedRedirectTarget);

  if (isAuthenticated && needsStytchOAuthLogin && stytchMemberSessionInitialized) {
    if (hasStytchMemberSession) {
      return <Navigate to={consumeAuthRedirectTarget() || '/'} replace />;
    }
  }

  if (isAuthenticated && !needsStytchOAuthLogin) {
    return (
      <Navigate
        to={resolveAuthenticatedRedirectTarget(user, consumeAuthRedirectTarget())}
        replace
      />
    );
  }

  if (!stytchPublicToken) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#637A6F',
          padding: '24px',
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <strong>Configuration Error</strong>
          <br />
          Stytch authentication is not configured. Ensure the server serves /config.js with
          STYTCH_PUBLIC_TOKEN or define VITE_STYTCH_PUBLIC_TOKEN at build time.
        </Alert>
      </div>
    );
  }

  const signInButtons = discoveryProviders.map((provider) => {
    const label = provider === 'microsoft' ? 'Continue with Microsoft' : 'Continue with Google';
    const Mark = provider === 'microsoft' ? MicrosoftMark : GoogleMark;
    return (
      <Button
        key={provider}
        variant="contained"
        onClick={() => handleProviderLogin(provider)}
        disabled={isAuthenticating || !stytchReady || !stytch}
        sx={{
          backgroundColor: '#ffffff',
          color: '#1f1f1f',
          fontFamily: "'Basis Grotesque', sans-serif",
          fontSize: '14px',
          fontWeight: 500,
          textTransform: 'none',
          borderRadius: '7px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          width: '240px',
          height: '40px',
          minWidth: '240px',
          minHeight: '40px',
          justifyContent: 'flex-start',
          padding: '0 14px',
          '&:hover': {
            backgroundColor: '#f8f8f8',
            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
          },
          '&:disabled': {
            backgroundColor: '#e0e0e0',
            color: '#9e9e9e',
          },
        }}
      >
        {isAuthenticating ? <CircularProgress size={18} sx={{ color: '#1f1f1f' }} /> : <Mark />}
        {isAuthenticating ? 'Signing in...' : label}
      </Button>
    );
  });
  const hasMicrosoftProvider = discoveryProviders.includes('microsoft');

  return (
    <div
      className="flex items-center justify-center px-6 py-10 md:px-10 md:py-12 lg:px-16"
      style={{
        minHeight: '100vh',
        width: '100%',
        background:
          'linear-gradient(-14.25deg, rgba(80, 90, 84, 0.8) 30.5%, rgba(189, 204, 190, 0.7) 108%), #000000',
        position: 'relative',
      }}
    >
      {/* Session expired message */}
      {status === 'expired' && sessionMessage && !dismissedSessionMessage ? (
        <Alert
          severity="warning"
          onClose={() => setDismissedSessionMessage(true)}
          sx={{
            position: 'absolute',
            top: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            maxWidth: '500px',
            zIndex: 1000,
          }}
        >
          {sessionMessage}
        </Alert>
      ) : null}

      <main
        data-testid="login-layout"
        className="flex w-full max-w-[1200px] flex-col items-center gap-8 md:gap-10 lg:flex-row lg:items-center lg:justify-between lg:gap-16"
      >
        <div
          data-testid="login-hero"
          className="order-1 w-full max-w-[760px] lg:order-2 lg:w-[56%] lg:max-w-none"
        >
          <img
            src="/images/member-landing-hero@2x.png"
            alt="Moments session reflection with transcript and video preview"
            className="block h-auto w-full object-contain"
          />
        </div>

        <section
          data-testid="login-content"
          className="order-2 flex w-full max-w-[560px] flex-col items-center text-center lg:order-1 lg:w-[44%] lg:max-w-[520px] lg:items-start lg:text-left"
        >
          <h1
            className="m-0 max-w-[471px] text-[42px] font-bold leading-[1.2] tracking-[-0.5px] text-white md:text-[56px] lg:text-[60px]"
            style={{ fontFamily: "'Basis Grotesque', sans-serif" }}
          >
            Every session holds more than you think
          </h1>

          <div
            className="mt-6 flex max-w-[471px] flex-col text-white md:mt-7"
            style={{
              fontFamily: "'Basis Grotesque', sans-serif",
              fontWeight: 400,
              fontSize: '20px',
              lineHeight: '1.6',
              letterSpacing: '0.15px',
            }}
          >
            <p style={{ margin: 0 }}>
              Moments surfaces what emerged from a coaching session. What shifted, what landed, and
              what&apos;s worth carrying forward.
            </p>
          </div>

          <div className="mt-8 flex flex-col items-center lg:items-start">
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '240px',
                  sm: `repeat(${signInButtons.length}, 240px)`,
                },
                gap: '24px',
                justifyContent: { xs: 'center', lg: 'start' },
              }}
            >
              {signInButtons}
            </Box>
            {import.meta.env.DEV && (
              <div className="flex flex-col gap-2 mt-4">
                <Button
                  variant="outlined"
                  onClick={() => handleDevLogin('dev@localhost')}
                  disabled={isAuthenticating}
                  sx={{
                    color: '#ffffff',
                    borderColor: 'rgba(255,255,255,0.5)',
                    textTransform: 'none',
                    fontSize: '13px',
                    '&:hover': { borderColor: '#ffffff', backgroundColor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  {isAuthenticating ? 'Signing in...' : 'Dev Login (admin)'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleDevLogin('member@localhost')}
                  disabled={isAuthenticating}
                  sx={{
                    color: '#90caf9',
                    borderColor: 'rgba(144,202,249,0.5)',
                    textTransform: 'none',
                    fontSize: '13px',
                    '&:hover': { borderColor: '#90caf9', backgroundColor: 'rgba(144,202,249,0.1)' },
                  }}
                >
                  {isAuthenticating ? 'Signing in...' : 'Dev Login (member)'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleDevLogin('sarah.chen@techcorp.io')}
                  disabled={isAuthenticating}
                  sx={{
                    color: '#a5d6a7',
                    borderColor: 'rgba(165,214,167,0.5)',
                    textTransform: 'none',
                    fontSize: '13px',
                    '&:hover': { borderColor: '#a5d6a7', backgroundColor: 'rgba(165,214,167,0.1)' },
                  }}
                >
                  {isAuthenticating ? 'Signing in...' : 'Dev Login (coach)'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleDevLogin('leonard.langsdorf@gmail.com')}
                  disabled={isAuthenticating}
                  sx={{
                    color: '#ce93d8',
                    borderColor: 'rgba(206,147,216,0.5)',
                    textTransform: 'none',
                    fontSize: '13px',
                    '&:hover': { borderColor: '#ce93d8', backgroundColor: 'rgba(206,147,216,0.1)' },
                  }}
                >
                  {isAuthenticating ? 'Signing in...' : 'Dev Login (Leonard member)'}
                </Button>
              </div>
            )}
            <div style={{ paddingTop: hasMicrosoftProvider ? '20px' : '0px' }}>
              <MentoLogoWhite width={98} />
            </div>
          </div>
        </section>
      </main>

      {/* Error feedback snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Loading overlay */}
      {isAuthenticating ? <AuthLoadingScreen overlay testId="login-auth-loading-screen" /> : null}
      <AuthDevPanel />
    </div>
  );
}

function LoginPageWithStytchSession() {
  const { session, isInitialized } = useStytchMemberSession();

  return (
    <LoginPageContent
      hasStytchMemberSession={!!session}
      stytchMemberSessionInitialized={isInitialized}
    />
  );
}

export default function LoginPage() {
  const { ready: stytchReady } = useStytch();

  if (stytchReady) {
    return <LoginPageWithStytchSession />;
  }

  return <LoginPageContent hasStytchMemberSession={false} stytchMemberSessionInitialized={false} />;
}
